import { useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Switch } from '@teo/components';
import { CrossFilled, Plus } from '@teo/components/icons';
import { useGet } from '@/query/fetchHooks';
import AddCompetenceLP from '@/components/Competencies/AddCompetenceLP';
import { AuthContext } from '@/components/Contexts/AuthContext';

export const useConditionCompetencies = (ids, onLoaded) => {
  const { auth } = useContext(AuthContext);
  const authUser = auth?.user;

  useGet(`/organisationcompetencedetails`, {
    competenceId: (ids || []).join(',OR,competenceId='),
    lang: authUser?.lang,
    organisationId: `${authUser?.activeOrganisationId},OR,organisationId=null`,
    enabled: (ids || []).length > 0,
    onSuccess: (result) => {
      const byId = new Map();
      (result?.data || []).forEach((comp) => {
        if (!byId.has(comp?.competenceId)) byId.set(comp?.competenceId, comp);
      });
      onLoaded(ids.map((id) => byId.get(id)).filter(Boolean));
    },
  });
};

export const conditionCompetenceIds = (list) => [
  ...new Set((list || []).map((comp) => comp?.competenceId)),
];

const CompetenceConditions = ({
  enabled,
  onToggle,
  competencies,
  onChange,
  className = '',
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [newCompetencies, setNewCompetencies] = useState([]);

  return (
    <div
      className={`flex flex-col rounded-lg border border-grey-02 p-4 ${className}`}
    >
      <div className="flex flex-row items-center gap-4 ">
        <Switch checked={!!enabled} onChange={(isCheck) => onToggle(isCheck)} />

        <div className="flex flex-col">
          <h4 className="font-normal">{t('links.competencies')}</h4>
          <p className="text-xs text-grey-05">
            {t('pages.learning_path.conditions.competencies_des')}
          </p>
        </div>
      </div>
      {enabled && (
        <>
          <div className="z-1 relative top-px mt-4 -ml-4 h-px w-[calc(100%_+_32px)] bg-[#EAEAED]"></div>
          <div className="relative pt-4">
            {competencies?.length > 0 && (
              <div className="mb-2 flex flex-wrap gap-2">
                {competencies.map((comp) => (
                  <div
                    key={comp?.competenceId}
                    className="flex rounded-md border bg-grey-01"
                  >
                    <div className="flex items-center gap-2 px-3 py-1 text-sm leading-none text-grey-08">
                      {comp?.qualificationCategoryColorCode && (
                        <span
                          style={{
                            backgroundColor:
                              comp?.qualificationCategoryColorCode,
                          }}
                          className={`h-1.5 w-1.5 min-w-1.5 rounded-full`}
                        ></span>
                      )}
                      <p className="leading-[1.3]">{comp.name}</p>

                      <div>
                        <Button
                          className="!px-1 !py-1"
                          variant="ghost"
                          onClick={() =>
                            onChange(
                              competencies.filter(
                                (el) => el?.competenceId !== comp?.competenceId
                              )
                            )
                          }
                        >
                          <CrossFilled className="h-4 w-4 text-grey-06" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {competencies?.length > 0 ? (
              <Button onClick={() => setIsOpen(true)} variant="outline">
                {t('pages.manage_competencies.add_competence')}
              </Button>
            ) : (
              <Button
                variant="ghost"
                className="flex w-full items-center gap-2 border !border-grey-02 !p-4 !text-[#1E1E2080]"
                onClick={() => setIsOpen(true)}
              >
                {t('pages.learning_path.no_competencies')}
                <Plus className="ml-auto w-5 text-[#4D4D50]" />
              </Button>
            )}
          </div>
        </>
      )}
      {isOpen && (
        <AddCompetenceLP
          conditions
          isOpenCompetencies={isOpen}
          setIsOpenCompetencies={setIsOpen}
          competencies={competencies || []}
          newCompetencies={newCompetencies}
          setNewCompetencies={setNewCompetencies}
          onSaveConditions={(list) => onChange(list || [])}
        />
      )}
    </div>
  );
};

export default CompetenceConditions;
